import { Fragment, useId, useMemo } from 'react';
import type { Riquadro, RigaRiquadro, ValoreRiquadro } from '@masamune/shared';
import { Icon } from '../../components/ui/Icon';
import { MRow } from '../../components/mobile/MobileKit';
import {
  clicCheApre, colonneNumeriche, conCentesimi, formattaValore, noteDaMostrare, quanteColonne, riquadroCompleto,
  righeTelefono, testoAltre, type PezzoRiga,
} from './riquadriTesto';
import './riquadri.css';

/**
 * Un riquadro coi numeri nella risposta di MUNE (Fase 2, fetta 2B «Chiedi», 29/10/2026;
 * os-planning/ai/fase-2-progetto.md, «I riquadri» e «Interfaccia»). I numeri li calcola il
 * codice e arrivano coi dati della rotta (evento `riquadro`): il modello non li scrive mai
 * e qui non si rileggono né si ricalcolano. È l'istantanea del turno: riaprendo la
 * conversazione si vede uguale, con la sua fonte e l'ora, e «Apri …» porta alla pagina vera.
 *
 * Computer: una tabella semplice (non .tbl: niente intestazione appiccicata in cima al
 * pannello che scorre), numeri a destra con le cifre tabellari, una riga col link si clicca
 * tutta e apre la pagina senza chiudere il pannello. Telefono: una lista di MRow, a destra
 * l'importo, sotto il resto; un tocco chiude il pannello e apre la pagina (lo fa `onLink`
 * del pannello). Le parole e i conti di forma stanno in riquadriTesto.ts.
 */
export function RiquadroChat({ riquadro, mobile, onLink }: {
  riquadro: Riquadro;
  mobile: boolean;
  onLink: (href: string) => void;
}): JSX.Element {
  const r = useMemo(() => riquadroCompleto(riquadro), [riquadro]);
  return mobile ? <RiquadroTelefono r={r} onLink={onLink} /> : <RiquadroComputer r={r} onLink={onLink} />;
}

const VUOTO: ValoreRiquadro = { t: 'vuoto' };

// ════════════════════════════════════════════════════════════════════
// Computer
// ════════════════════════════════════════════════════════════════════

function RiquadroComputer({ r, onLink }: { r: Riquadro; onLink: (href: string) => void }) {
  const idTitolo = useId();
  const numeriche = colonneNumeriche(r);
  const colonne = quanteColonne(r);
  const centesimiRighe = conCentesimi(r.tipo, 'righe');
  const centesimiTotali = conCentesimi(r.tipo, 'totali');
  const altre = testoAltre(r);

  return (
    <div className="rq" role="group" aria-labelledby={idTitolo} data-tipo={r.tipo}>
      <h3 className="rq-titolo" id={idTitolo}>{r.titolo}</h3>

      {/* Nella colonna da 400 px una tabella con un nome lungo e uno stato lungo può non
          starci: scorre di lato dentro il riquadro invece di finire tagliata dal pannello. */}
      {r.righe.length > 0 && (
        <div className="rq-scorre">
          <table className="rq-tab" aria-labelledby={idTitolo}>
            <thead>
              <tr>
                {Array.from({ length: colonne }, (_, i) => (
                  <th key={i} scope="col" className={numeriche[i] ? 'rq-num' : undefined}>{r.colonne[i] ?? ''}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {r.righe.map((riga, i) => (
                <RigaComputer key={i} riga={riga} colonne={colonne} numeriche={numeriche} centesimi={centesimiRighe} onLink={onLink} />
              ))}
            </tbody>
          </table>
        </div>
      )}
      {altre && <p className="rq-altre">{altre}</p>}

      {r.totali.length > 0 && (
        <dl className="rq-totali">
          {r.totali.map((t, i) => (
            <div key={i} className="rq-totale">
              <dt>{t.etichetta}</dt>
              <dd><Valore v={t.valore} centesimi={centesimiTotali} /></dd>
            </div>
          ))}
        </dl>
      )}

      <Fondo r={r} onLink={onLink} />
    </div>
  );
}

/**
 * Una riga della tabella. Col link si clicca tutta (sfondo --row-hover); per la tastiera e i
 * lettori di schermo il link vero sta nella prima cella, così la tabella resta una tabella.
 */
function RigaComputer({ riga, colonne, numeriche, centesimi, onLink }: {
  riga: RigaRiquadro;
  colonne: number;
  numeriche: boolean[];
  centesimi: boolean;
  onLink: (href: string) => void;
}) {
  const href = riga.link;
  const apri = href ? clicCheApre(href, onLink) : undefined;
  return (
    <tr className={href ? 'rq-riga rq-riga--link' : 'rq-riga'} onClick={apri}>
      {Array.from({ length: colonne }, (_, i) => {
        const v = riga.celle[i] ?? VUOTO;
        const valore = <Valore v={v} centesimi={centesimi} />;
        // Importi e giorni non vanno a capo; testi e stati sì, se no la tabella non ci sta.
        const cls = numeriche[i] ? 'rq-num' : v.t === 'giorno' ? 'rq-giorno' : undefined;
        return (
          <td key={i} className={cls}>
            {i === 0 && href ? (
              <a className="rq-link" href={href} onClick={apri}>
                {formattaValore(v, { centesimi }) ? valore : 'Apri'}
              </a>
            ) : valore}
          </td>
        );
      })}
    </tr>
  );
}

/**
 * Un valore: testo, importo o stato. Lo stato è scritto col colore del suo tono, come al
 * telefono (29/10/2026): nella colonna da 400 px una pastiglia andava a capo su due righe
 * («Da / incassare») e sembrava rotta.
 */
function Valore({ v, centesimi }: { v: ValoreRiquadro; centesimi: boolean }) {
  const testo = formattaValore(v, { centesimi });
  if (!testo) return null;
  if (v.t === 'stato') return <span className="rq-tono" data-tono={v.tono}>{testo}</span>;
  return <>{testo}</>;
}

/**
 * Sotto le righe, uguale al computer e al telefono: le note del codice, la fonte con l'ora
 * (col pallino rosso se i dati sono fermi o il fornitore non risponde) e «Apri …».
 */
function Fondo({ r, onLink }: { r: Riquadro; onLink: (href: string) => void }) {
  const note = noteDaMostrare(r);
  return (
    <>
      {note.map((n, i) => <p key={i} className="rq-nota">{n}</p>)}
      {r.fonte.testo && (
        <p className="rq-fonte" data-problema={r.fonte.problema ? 'true' : undefined}>
          {r.fonte.problema && <span className="rq-pallino" aria-hidden="true" />}
          <span>{r.fonte.testo}</span>
        </p>
      )}
      {r.apri && (
        <a className="btn btn-sm btn-ghost rq-apri" href={r.apri.href} onClick={clicCheApre(r.apri.href, onLink)}>
          {r.apri.testo}
          <Icon name="arrowRight" size={12} />
        </a>
      )}
    </>
  );
}

// ════════════════════════════════════════════════════════════════════
// Telefono
// ════════════════════════════════════════════════════════════════════

function RiquadroTelefono({ r, onLink }: { r: Riquadro; onLink: (href: string) => void }) {
  const idTitolo = useId();
  const righe = righeTelefono(r);
  const centesimiTotali = conCentesimi(r.tipo, 'totali');
  const altre = testoAltre(r);
  const conLista = r.righe.length > 0 || r.totali.length > 0;

  return (
    <div className="rq rq--m" role="group" aria-labelledby={idTitolo} data-tipo={r.tipo}>
      <h3 className="rq-titolo" id={idTitolo}>{r.titolo}</h3>

      {conLista && (
        <div className="mk-list">
          {righe.map((t, i) => {
            const href = t.link;
            return (
              <MRow
                key={i}
                title={t.titolo}
                sub={t.sotto.length ? <Pezzi pezzi={t.sotto} /> : undefined}
                subWrap
                trail={t.coda?.testo}
                trailTone={codaTono(t.coda)}
                trailSub={t.nomeCoda ?? undefined}
                onClick={href ? () => onLink(href) : undefined}
              />
            );
          })}
          {altre && <div className="rq-m-altre">{altre}</div>}
          {r.totali.length > 0 && (
            <div className="rq-m-totali">
              {r.totali.map((t, i) => {
                const valore = formattaValore(t.valore, { centesimi: centesimiTotali });
                const tono = t.valore.t === 'stato' ? t.valore.tono : null;
                return (
                  <MRow
                    key={i}
                    title={t.etichetta}
                    trail={valore || undefined}
                    trailTone={tono === 'alert' ? 'alert' : undefined}
                  />
                );
              })}
            </div>
          )}
        </div>
      )}

      <Fondo r={r} onLink={onLink} />
    </div>
  );
}

/** A destra: rosso per lo stato `alert` (il saldo sotto zero), grigio per `mute`. */
function codaTono(p: PezzoRiga | null): 'alert' | 'mute' | undefined {
  if (p?.tono === 'alert') return 'alert';
  if (p?.tono === 'mute') return 'mute';
  return undefined;
}

/** La riga sotto: le celle unite da « · », gli stati col loro colore. */
function Pezzi({ pezzi }: { pezzi: PezzoRiga[] }) {
  return (
    <>
      {pezzi.map((p, i) => (
        <Fragment key={i}>
          {i > 0 && ' · '}
          {p.tono ? <span className="rq-tono" data-tono={p.tono}>{p.testo}</span> : p.testo}
        </Fragment>
      ))}
    </>
  );
}
